import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Dashboard } from '../../api';

export function AdminHome() {
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<Dashboard>('/admin/dashboard').then(setData).catch((e) => setError(e.message));
  }, []);

  if (error) return <p className="auth__error">{error}</p>;
  if (!data) return <p className="muted">Loading…</p>;
  const c = data.counts;

  return (
    <>
      <h1 className="h1">Overview</h1>

      <div className="tiles">
        <Tile label="People" value={c.activePeople} note={c.people - c.activePeople > 0 ? `${c.people - c.activePeople} deactivated` : 'all active'} to="/admin/people" />
        <Tile label="Courses live" value={c.activeCourses} note={`${c.courses - c.activeCourses} switched off`} to="/admin/courses" tone={c.activeCourses === 0 ? 'warn' : undefined} />
        <Tile label="Cohorts" value={c.cohorts} to="/admin/cohorts" />
        <Tile label="Enrolments" value={c.enrolments} note={`${c.completed} completed`} />
      </div>

      {c.people - c.activePeople > 0 && (
        <p className="muted small">
          {(c.people - c.activePeople).toLocaleString()} of {c.people.toLocaleString()} accounts are deactivated —
          people who have finished their training. Their records are kept in full. If somebody needs to study
          again, switch them back on from <Link to="/admin/people?status=inactive">the deactivated list</Link>.
        </p>
      )}

      {c.awaitingFirstPassword > 0 && (
        <p className="notice">
          <strong>{c.awaitingFirstPassword}</strong> {c.awaitingFirstPassword === 1 ? 'person has' : 'people have'} not
          set a password yet. They were brought over from TalentLMS, which does not export passwords, so each of them
          chooses one the first time they sign in.
        </p>
      )}

      {c.awaitingMarking > 0 && (
        <p className="notice notice--warn">
          <strong>{c.awaitingMarking}</strong> test {c.awaitingMarking === 1 ? 'attempt is' : 'attempts are'} waiting
          for someone to mark the written answers.
        </p>
      )}

      <h2 className="h2">Progress across all enrolments</h2>
      <ProgressBar completed={c.completed} inProgress={c.inProgress} notStarted={c.notStarted} />

      <h2 className="h2">Busiest courses</h2>
      <table className="grid">
        <thead>
          <tr><th>Course</th><th>Enrolled</th><th>Completed</th><th /></tr>
        </thead>
        <tbody>
          {data.busiest.map((b) => (
            <tr key={b.id}>
              <td>
                <Link to={`/admin/courses/${b.id}`}>{b.name}</Link>
                {b.status !== 'active' && <span className="tag">off</span>}
              </td>
              <td className="num">{b.enrolled}</td>
              <td className="num">{b.completed}</td>
              <td className="num muted">{b.enrolled ? `${Math.round((b.completed / b.enrolled) * 100)}%` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function Tile({ label, value, note, to, tone }: { label: string; value: number; note?: string; to?: string; tone?: 'warn' }) {
  const inner = (
    <>
      <span className="tile__value">{value.toLocaleString()}</span>
      <span className="tile__label">{label}</span>
      {note && <span className="tile__note">{note}</span>}
    </>
  );
  return to
    ? <Link className={`tile ${tone ? 'tile--warn' : ''}`} to={to}>{inner}</Link>
    : <div className={`tile ${tone ? 'tile--warn' : ''}`}>{inner}</div>;
}

function ProgressBar({ completed, inProgress, notStarted }: { completed: number; inProgress: number; notStarted: number }) {
  const total = completed + inProgress + notStarted || 1;
  const pct = (n: number) => `${(n / total) * 100}%`;
  return (
    <>
      <div className="stack" role="img" aria-label={`${completed} completed, ${inProgress} in progress, ${notStarted} not started`}>
        <span className="stack__done" style={{ width: pct(completed) }} />
        <span className="stack__doing" style={{ width: pct(inProgress) }} />
        <span className="stack__todo" style={{ width: pct(notStarted) }} />
      </div>
      <p className="legend">
        <span><i className="dot dot--done" /> {completed.toLocaleString()} completed</span>
        <span><i className="dot dot--doing" /> {inProgress.toLocaleString()} in progress</span>
        <span><i className="dot dot--todo" /> {notStarted.toLocaleString()} not started</span>
      </p>
    </>
  );
}
